import axios from "axios";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, getEventById, getTimeSlotsByEventId } from "ticketino-api-client";
import { Calendar } from "react-calendar";
import { toZonedTime } from 'date-fns-tz';

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Loading from "./components/Loading";
import { DateTimeManager } from '../Common/DateTimeManager.js';
import imgTimeIcon from '../img/ticketing/icon-time.svg';
import imgNoPhotography from '../img/ticketing/icon-nophotography.svg';
import imgCoatroom from '../img/ticketing/icon-coatroom.svg';
import imgLivePerformance from '../img/ticketing/icon-live-performance.svg';
import imgTriggerWarning from '../img/ticketing/icon-trigger-warning.svg';
import imgClose from '../img/icon-close.svg';
import imgIconProgress1 from '../img/icon-progress1.svg';
import imgIconProgressGrey from '../img/icon-progress-grey.svg';
import anleitungpdf from '../files/anleitung_ticketbestellung_abramovic.pdf';


import "../css/style.css";
import "../css/calendar.css";
import "../css/ticketing.css";

const Step10_Calendar_Abramovic = () => {
    const [posId, setPosId] = useState(null);

    const [event, setEvent] = useState({});

    const [eventsUpgrade, setEventsUpgrade] = useState([]);
    const [eventGroup, setEventGroup] = useState({});
    const [isTourAvailable, setIsTourAvailable] = useState(false);
    const [orderId, setOrderId] = useState(0);
    const [order, setOrder] = useState({});

    const [eventGroupIdAbramovic, setEventGroupIdAbramovic] = useState(0);

    const [eventDates, setEventDates] = useState([]);

    const [date, setDate] = useState(new Date());
    const [timeSlots, setTimeSlots] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedEventId, setSelectedEventId] = useState(0);
    const [selectedHour, setSelectedHour] = useState(null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState({});

    const timeZone = 'Europe/Zurich';
    const today = new Date();
    const zurichDate = toZonedTime(today, timeZone);  // Convert to Zurich time

    const defaultMonth = zurichDate.getMonth() < 9 ? 9 : zurichDate.getMonth(); // Month based on Zurich time
    const [currentMonth, setCurrentMonth] = useState(new Date(Date.UTC(zurichDate.getFullYear(), defaultMonth, 1)));

    const [isValid, setIsValid] = useState(false);
    const [loading, setLoading] = useState(true);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);


    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const { language } = useParams();

    let languageId = 0;

    // changing languageId according to the url
    switch (language) {
        case ("de" || "DE"):
            languageId = 1;
            break;
        case ("fr" || "FR"):
            languageId = 2;
            break;
        case ("en" || "EN"):
            languageId = 3;
            break;
        case ("it" || "IT"):
            languageId = 4;
            break;
        default:
            languageId = 0;
            break;
    }

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    const [resources, setResources] = useState({});

    useEffect(() => {

        setLoading(true);
        const loadToken = async () => {
            try {
                const res = await axios.get("form/token");
                axios.defaults.headers.common["Authorization"] = "Bearer " + res.data;
                api.defaults.headers.common['Authorization'] = "Bearer " + res.data;
                await sessionStorage.setItem("token", res.data);
                requestFormSettings();
            } catch (error) {
                console.error(error);
            }
        };

        let currentOrderId = sessionStorage.getItem("OrderId");

        if (currentOrderId > 0) {
            setOrderId(currentOrderId);
        }

        const requestFormSettings = async () => {
            try {
                const res = await axios.get(`form/formsettings`);

                if (currentOrderId > 0) {
                    loadOrder(currentOrderId)
                } else {
                    startOrder(res.data.posId);
                }

                setPosId(res.data.posId)
                setEventGroupIdAbramovic(res.data.eventGroupIdAbramovic);
            } catch (error) {
                console.error(error);
            }
        };

        loadToken();
        setLoading(false);

    }, []);


    useEffect(() => {

        setLoading(true);

        const requestResources = async () => {
            try {
                const res = await axios.get(`form/resources/${language}`);
                setResources(res.data?.translation);
            } catch (error) {
                console.error(error);
            }
        };

        requestResources();
        setLoading(false);
    }, [language]);


    useEffect(() => {

        if (Object.keys(selectedTimeSlot).length > 0) {

            if (sessionStorage.getItem("BeginTimeAbramovic") && sessionStorage.getItem("EndTimeAbramovic")) {
                navigate(`/${language}/marina-abramovic-tickets/${selectedTimeSlot.eventId}`);
            } else {
                console.error("Session storage values not set!");
            }
        }

    }, [selectedTimeSlot]);


    useEffect(() => {
        if (orderId > 0 && eventGroupIdAbramovic > 0) {
            loadUpgradeEvents(eventGroupIdAbramovic, currentMonth);
        }
    }, [currentMonth, eventGroupIdAbramovic]);

    const selectedDateUTC = useMemo(() => {
        if (selectedDate) {
            return new Date(Date.UTC(
                selectedDate.split('-')[0],
                selectedDate.split('-')[1] - 1,  // Month (0-indexed)
                selectedDate.split('-')[2]
            ));
        }
    }, [selectedDate]);

    useEffect(() => {
        (async () => {
            let timeslotsByEventIds = [];

            if (selectedDateUTC) {
                const currentEventsByDate = eventsUpgrade.filter(e => {
                    // Convert event start to UTC date for comparison
                    const eventDateUTC = new Date(e.start);
                    // Compare only the date part for matching
                    return eventDateUTC.toISOString().split('T')[0] === selectedDate;
                });

                for (const event of currentEventsByDate) {
                    let timeslotsOfEvent = await getTimeslotsOfEvent(event.id);

                    if (timeslotsOfEvent && timeslotsOfEvent.length > 0) {
                        // Map over timeslotsOfEvent to add the event id to each timeslot
                        const timeslotsWithEventId = timeslotsOfEvent.map(timeslot => ({
                            ...timeslot,
                            eventId: event.id
                        }));

                        timeslotsByEventIds = [...timeslotsByEventIds, ...timeslotsWithEventId];
                    }
                }
            }

            setTimeSlots(timeslotsByEventIds);
        })();
    }, [selectedDateUTC]);

    const startOrder = async (posId) => {

        const order = {
            affiliateShopId: null,
            currency: "CHF",
            tenantId: 1,
            pointOfSaleId: posId ?? 6585,
            abbreviation: "",
            paymentType: 1,
        };

        try {
            const res = await axios.post(`${baseUrl}/ShopBasket/Order`, order);

            await sessionStorage.setItem("OrderId", res.data.id);

            setOrderId(res.data.id);
            setCurrentMonth(new Date(Date.UTC(zurichDate.getFullYear(), defaultMonth, 1)));

            return res.data;
        }
        catch (error) {
            console.error(error);
        }
    };

    const loadOrder = async (orderId) => {
        try {
            // Order
            const updatedOrder = await getOrderByOrderId(orderId);

            //const bookedTickets = updatedOrder.tickets;

            // Booked Tickets
            //const updatedTickets = await Promise.all(
            //    bookedTickets.map(async (ticket) => {
            //        const ticketType = await getTicketTypeById(ticket.ticketTypeId);

            //        const info = await getTicketTypeInfosByTicketTypeId(ticket.ticketTypeId);
            //        const ticketTypeInfo = info.find(info => info.languageId == languageId) ?? info[0];

            //        return { ...ticket, ticketType: ticketType, info: ticketTypeInfo }
            //    })
            //)

            if (updatedOrder != null && updatedOrder.tickets != null) {

                let ticketTypeIdsToRemove = updatedOrder.tickets
                    .map(ticket => ticket.id);

                // DELETE request to remove tickets
                await axios.delete(`${baseUrl}/ShopBasket/Order/${orderId}/Tickets`, {
                    data: { TicketsToRemove: ticketTypeIdsToRemove }
                });

                updatedOrder.tickets = [];
            }

            setOrder(updatedOrder);

            //validate page if user can proceed
            let quantity = 0;

            if (updatedOrder != null && updatedOrder.tickets != null) {
                quantity = updatedOrder.tickets.length;
            }

            if (quantity > 0) {
                setIsValid(true);
            } else {
                setIsValid(false);
            }
        }
        catch (error) {
            console.error(error);
        }
    }

    const getOrderByOrderId = async (orderId) => {
        try {
            const res = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}`);
            return res.data;
        }
        catch (error) {
            console.error(error);
            if (error.response.status === 404 || error.response.status === 403) {
                const newOrder = await startOrder(posId);
                return newOrder;
            }
        }
    }

    const loadUpgradeEvents = async (eventGroupIdUpgrade, currentMonth) => {
        setLoading(true);

        try {
            // EventGroup
            const eventGroup = await axios.get(`${baseUrl}/EventGroup/${eventGroupIdUpgrade}`);

            const totalNumberOfEventsResponse = await axios.get(`${baseUrl}/EventGroup/${eventGroupIdUpgrade}/Events/Count`);
            const totalNumberOfEvents = totalNumberOfEventsResponse.data;
            const eventBatchSize = 100;
            let events = [];

            for (let i = 0; i <= totalNumberOfEvents; i += eventBatchSize) {
                const eventGroupEventsResponse = await axios.get(`${baseUrl}/EventGroup/Events?eventGroupId=${eventGroupIdUpgrade}&languageCode=${language}&numberToTake=${eventBatchSize}&numberToSkip=${i}`);
                events = events.concat(eventGroupEventsResponse.data?.events);
            }
            
            // Check if the event date is within the current month and in the future
            const eventsForCurrentMonth = events.filter((event) => {
                const eventDate = new Date(event.end);

                //// add 10 hours to the event start time
                //eventDate.setHours(eventDate.getHours() + 10); //workaround until event.end can be used

                const startOfMonth = new Date(Date.UTC(currentMonth.getFullYear(), currentMonth.getMonth(), 1));
                const endOfMonth = new Date(Date.UTC(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
                return eventDate >= startOfMonth && eventDate < endOfMonth && eventDate >= today;
            });


            const datesArray = [];

            await Promise.all(eventsForCurrentMonth.map(async (event) => {
                const ticketCountResponse = await requestTicketCount(event.id);
                const theEvent = await getEventById(event.id);

                let soldOut = (theEvent?.maxTickets - ticketCountResponse) <= 0;

                if (theEvent.status === 0) {
                    soldOut = true;
                }

                if (!soldOut) {
                    datesArray.push(event.start?.split('T')[0]);
                }

                event.end = theEvent?.end;
                event.isSoldOut = soldOut;
                event.maxTickets = theEvent?.maxTickets;
                event.ticketCount = theEvent?.maxTickets - ticketCountResponse;
                event.eventGroupId = eventGroupIdAbramovic;

            })).then(() => {
                setEventDates(datesArray);
                setEventsUpgrade(events);
                setEventGroup(eventGroup.data)
                setLoading(false);
            });
        } catch (error) {
            console.error(error);
        }
    };

    const requestTicketCount = async (id) => {
        try {
            const res = await axios.get(`${baseUrl}/Event/${id}/TicketsSold`);
            return res.data;
        } catch (error) {
            console.error(error);
        }
    };

    const tileClassName = ({ date }) => {
        // Create a UTC date from the input
        const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        // Format the date as ISO string in UTC
        const formattedDate = utcDate.toISOString().split('T')[0];
        return eventDates.includes(formattedDate) ? 'bold-date' : 'disabled-date';
    };

    const handleActiveStartDateChange = ({ activeStartDate }) => {
        setCurrentMonth(activeStartDate);

        // clear old values
        setSelectedDate("");
        setSelectedHour(null);
        setSelectedEventId(0);
        setIsTourAvailable(false);
    };

    const onClickDay = (date) => {
        let selectedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const formattedDate = selectedDate.toISOString().split('T')[0];
        setSelectedDate(formattedDate);

        // Compare against the UTC date for '2024-10-25'
        const comparisonDate = new Date(Date.UTC(2024, 9, 25)); // October is month 9 (0-indexed)
        if (selectedDate < comparisonDate) {
            setIsTourAvailable(false);
        } else {
            setIsTourAvailable(true);
        }

        // Clear old values
        setSelectedEventId(0);
        setSelectedHour(null);

        // gtm - select_date
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'select_date',
            'date': formattedDate,
        });
    };

    const onCheckedChange = (e) => setSelectedEventId(e.target.value);

    const getTimeslotsOfEvent = async (eventId) => {
        let timeslotsByEventId = await getTimeSlotsByEventId(eventId);

        return timeslotsByEventId;
    };

    const onTimeSlotClick = async (timeSlot) => {
        await sessionStorage.setItem("BeginTimeAbramovic", timeSlot.beginTime);
        await sessionStorage.setItem("EndTimeAbramovic", timeSlot.endTime);

        // gtm - select_time_slot
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'select_time_slot',
            'timeSlotBegin': timeSlot.beginTime,
            'timeSlotEnd': timeSlot.endTime
        });

        setSelectedTimeSlot(timeSlot);
    };

    const getHourFromTimeString = (timeString) => {
        const [hours] = timeString.split(':');
        return parseInt(hours, 10);
    };

    const closeTimeslotPopup = () => {
        setDate("");
        setSelectedDate("");
        setIsTourAvailable(false);
    }

    const formatTimeForCurrentTimeForTimeSlot = (date) => {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const mapEventStartTimes = () => {
        const currentEventsByDate = eventsUpgrade.filter(e => e.start?.split('T')[0] === selectedDate);

        const groupedTimeslots = [];


        if (timeSlots?.length > 0) {

            timeSlots.forEach((slot) => {
                const hour = getHourFromTimeString(slot.beginTime);
                if (!groupedTimeslots[hour]) {
                    groupedTimeslots[hour] = [];
                }
                groupedTimeslots[hour].push(slot);
            });
        }

        let selectedHoursIndex = groupedTimeslots.length - 1;

        if (Object.keys(groupedTimeslots).length > 0 && selectedHour != null) {
            selectedHoursIndex = Object.keys(groupedTimeslots).indexOf(selectedHour);
        }

        const currentTime = new Date(); // Get current date and time

        const currentTimeInZurich = toZonedTime(currentTime, timeZone);
        const formattedCurrentTimeZurich = formatTimeForCurrentTimeForTimeSlot(currentTimeInZurich);
        
        const yearObj = currentTimeInZurich.getFullYear();
        const monthObj = currentTimeInZurich.getMonth();
        const dayObj = currentTimeInZurich.getDate();
        const hourObj = currentTimeInZurich.getHours();

        // Compare the current date with event selected date
        const [yearStr, monthStr, dayStr] = selectedDate.split('-');

        const isSameDate = (
            yearObj === parseInt(yearStr) &&
            monthObj === parseInt(monthStr) - 1 &&
            dayObj === parseInt(dayStr)
        );

        const lastHour = parseInt(Object.keys(groupedTimeslots).pop()) + 1; // gets the last hour and add 1 for closing time
        
        return (
            <>
                {Object.keys(groupedTimeslots).length > 0 && (
                    <>
                        <div>
                            <div>
                                {Object.keys(groupedTimeslots).map((hour, index) => (
                                    <div>
                                        {(groupedTimeslots[hour].reduce((acc, curr) => acc + (curr.totalAvailableTickets - curr.totalTicketsSold), 0) > 0) && (hourObj <= hour || !isSameDate) &&
                                            <div className={hour === selectedHour ? "ticketing-timeslot-container-Selected" : "ticketing-timeslot-container"}>
                                                <div>
                                                    {index === 0 &&
                                                        <div className="grey-line" ></div>}
                                                    <div className={selectedHoursIndex === index ? "timslot-time-header-selected" : "timslot-time-header"} key={hour} onClick={() => setSelectedHour(hour)}>
                                                        {hour}:00-{parseInt(hour) + 1}:00
                                                    </div>
                                                    <div onClick={() => setSelectedHour(hour)}><p className={selectedHoursIndex === index ? "timeslot-inner-text-selected" : "timeslot-inner-text"}>{groupedTimeslots[hour].reduce((acc, curr) => acc + (curr.totalAvailableTickets - curr.totalTicketsSold), 0)} {resources?.PlacesFree}</p>
                                                    </div>

                                                    {/* Display museum closing time for the list time slot hour span */}
                                                    {index === Object.keys(groupedTimeslots).length - 1 && selectedHoursIndex === index && (
                                                        <div className="timeslot-last-time">
                                                            {resources?.TicketCalendarMuseumCloseTime.replace("{hour}", `${lastHour}:00`)}
                                                        </div>
                                                    )}

                                                    {(((Object.keys(groupedTimeslots).length - 1) !== index && selectedHoursIndex - 1 !== index) ||
                                                        (selectedHoursIndex === index)) &&
                                                        <div className="grey-line" ></div>}
                                                    {selectedHour === hour &&
                                                        <div className="ticketing-timeslot-div">
                                                            {groupedTimeslots[selectedHour].map((timeSlot, timeSlotIndex) => (
                                                                <>
                                                                    {(formattedCurrentTimeZurich < timeSlot.endTime || !isSameDate) && 
                                                                        <div key={timeSlot.timeSlotId} className={timeSlot.totalTicketsSold >= timeSlot.totalAvailableTickets ? "ticketing-disabled row" : "row"}>
                                                                            <div className="col">
                                                                                <div className="timslot-time-header">{timeSlot.beginTime.split(':').slice(0, 2).join(':')}-{timeSlot.endTime.split(':').slice(0, 2).join(':')}</div>
                                                                                <p className="timeslot-inner-text">{timeSlot.totalAvailableTickets - timeSlot.totalTicketsSold} {resources?.PlacesFree}</p>
                                                                            </div>
                                                                            <div className="col d-flex align-items-center justify-content-end" disabled={timeSlot.totalTicketsSold >= timeSlot.totalAvailableTickets}>
                                                                                <button className="btn custom-button" onClick={() => onTimeSlotClick(timeSlot)}>
                                                                                    <svg width="30" height="25" viewBox="0 0 39 34" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="svg-ticket">
                                                                                        <path fill="evenodd" fillRule="evenodd" clip-rule="evenodd" d="M0.340034 17.4648C0.383166 17.4864 28.3505 23.097 28.3972 23.097C28.6417 23.0863 28.8789 22.9353 28.9256 22.6981L29.0766 21.9433C29.1233 21.7025 28.9651 21.4724 28.7423 21.3682C27.9947 21.0159 27.5525 20.1893 27.7215 19.341C27.8904 18.4927 28.6201 17.8997 29.4468 17.8637C29.6912 17.8529 29.9284 17.702 29.9751 17.4648L30.0866 16.9112L30.1261 16.7064C30.1729 16.4655 30.0147 16.2355 29.7919 16.1313C29.0442 15.779 28.6021 14.9523 28.7711 14.1041C28.94 13.2558 29.6697 12.6628 30.4964 12.6268C30.7408 12.616 30.978 12.4651 31.0247 12.2279L31.1757 11.473C31.2224 11.2322 31.0642 11.0022 30.8414 10.898C30.0938 10.5457 29.6517 9.71902 29.8206 8.87076C29.9896 8.0225 30.7192 7.42944 31.5459 7.3935C31.7903 7.38272 32.0275 7.23176 32.0743 6.99453L32.2432 6.15706C32.2899 5.91624 32.1353 5.68261 31.8945 5.63588C31.8945 5.63588 3.79061 0 3.76186 0C3.55339 0 3.37008 0.147367 3.32694 0.355837L3.15801 1.19331C3.11128 1.43413 3.26943 1.66417 3.49228 1.7684C4.2399 2.12064 4.682 2.94734 4.51307 3.79559C4.34413 4.64385 3.61449 5.23691 2.7878 5.27286C2.54335 5.28364 2.30616 5.4346 2.25943 5.67182L2.10847 6.42663C2.06175 6.66745 2.2199 6.89749 2.44274 7.00172C3.19036 7.35396 3.6325 8.18066 3.46353 9.02891C3.29459 9.87717 2.56495 10.4702 1.73826 10.5062C1.49384 10.517 1.25662 10.6679 1.20989 10.9051L1.05893 11.6599C1.01221 11.9008 1.17039 12.1308 1.3932 12.235C2.14086 12.5873 2.58296 13.414 2.41399 14.2622C2.24506 15.1105 1.51541 15.7036 0.688718 15.7395C0.444305 15.7503 0.20708 15.9013 0.160354 16.1385L0.00939317 16.8933C-0.0409631 17.1341 0.117151 17.3606 0.340034 17.4648ZM18.2482 16.8646L21.1173 17.4494L16.3421 11.0142L22.1485 7.61997L19.4053 7.06091L14.6995 9.80457L12.1677 9.2886L12.8905 5.74221L10.8119 5.31861L8.84924 14.9491L10.9278 15.3727L11.8036 11.0752L14.33 11.5901L18.2482 16.8646ZM3.88854 20.93C3.82936 20.8864 3.77694 20.8357 3.73415 20.7808L30.2022 26.0238L32.0848 16.5204L37.8675 19.1999C38.105 19.3085 38.2252 19.5593 38.1395 19.7603L37.8348 20.4578C37.7496 20.6557 37.4843 20.7238 37.2339 20.6654C36.3872 20.4671 35.5508 20.7912 35.2444 21.4983C34.9379 22.2053 35.2565 23.0633 35.9612 23.5846C36.1714 23.7393 36.2958 23.9878 36.2101 24.1889L35.9367 24.8179C35.8516 25.0158 35.5863 25.0839 35.3359 25.0255C34.4892 24.8272 33.6528 25.1513 33.3463 25.8584C33.0398 26.5654 33.3585 27.4234 34.0632 27.9447C34.2734 28.0994 34.3978 28.3479 34.3121 28.549L34.2393 28.7201L34.0381 29.1812C33.9529 29.3791 33.6877 29.4472 33.4373 29.3887C32.5905 29.1905 31.7541 29.5146 31.4477 30.2216C31.1412 30.9287 31.4599 31.7867 32.1646 32.308C32.3748 32.4627 32.4992 32.7112 32.4134 32.9123L32.1401 33.5413C32.0549 33.7392 31.7897 33.8073 31.5393 33.7488C31.4917 33.7358 3.929 20.9612 3.88854 20.93Z"></path>
                                                                                    </svg>
                                                                                    <span> {resources?.Tickets}</span>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    }
                                                                </>
                                                            ))}
                                                            {isTourAvailable && (hourObj <= hour || !isSameDate) && <div>
                                                                <div className="guided-tour-text">{resources?.FancyTourQuestion} </div>
                                                                <div className="timeslots-guided-tour-button-container">
                                                                    <button className="btn transperant-grey-button" onClick={() => navigate(`/${language}/marina-abramovic-tours`)}>
                                                                        {resources?.BookGuidedTour}
                                                                    </button>
                                                                </div>
                                                            </div>}
                                                        </div>}
                                                </div>
                                            </div>}
                                        {!(groupedTimeslots[hour].reduce((acc, curr) => acc + (curr.totalAvailableTickets - curr.totalTicketsSold), 0) > 0) && (hourObj <= hour || !isSameDate) &&
                                            <div className="ticketing-timeslot-container ticketing-disabled">
                                                <div>
                                                    <div className={selectedHoursIndex === index ? "timslot-time-header-selected" : "timslot-time-header"} key={hour} onClick={() => setSelectedHour(hour)}>
                                                        {hour}:00-{parseInt(hour) + 1}:00
                                                    </div>
                                                    <p className={selectedHoursIndex === index ? "timeslot-inner-text-selected" : "timeslot-inner-text"}> {groupedTimeslots[hour].reduce((acc, curr) => acc + (curr.totalAvailableTickets - curr.totalTicketsSold), 0)} {resources?.PlacesFree}</p>
                                                    {/*<div className="grey-line" >{selectedHoursIndex} {index}  {Object.keys(groupedTimeslots).length}</div>*/}
                                                    {(((Object.keys(groupedTimeslots).length - 1) !== index && selectedHoursIndex - 1 !== index) ||
                                                        (selectedHoursIndex === index)) &&
                                                        <div className="grey-line" ></div>}
                                                </div>
                                            </div>}
                                    </div>
                                ))}
                            </div>
                        </div>

                    </>)
                }
            </>
        );
    };

    const formatTime = (date) => {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const formatMonth = (locale, date) => {
        return date.toLocaleDateString(locale, { month: 'long' });
    };

    return (
        <div className="d-flex flex-column min-vh-100">
            {loading && <Loading alignment="center" color="#d3d3d3" bgColor="transparent" position="fixed" top="300px" />}
            <>
                <div>
                    <Navbar numberOfTickets={order?.tickets?.length ?? 0} isShoppingCartVisible={true} pagePath="marina-abramovic" />
                    <main className="flex-fill">
                        {resources && (
                            <div className="container page-wrapper">
                                <div>
                                    <div className="progress-container">
                                        <div className="progress-step active">
                                            <div>
                                                <div className="image">
                                                    <img src={imgIconProgress1} alt="Step 1" />
                                                </div>
                                                <div className="step-number text-wrap visible-desktop">{resources?.DateAndTime}</div>
                                            </div>
                                            <div className="line"></div>
                                        </div>

                                        <div className="progress-step completed">
                                            <div>
                                                <div className="image">
                                                    <img src={imgIconProgressGrey} alt=" Step 2" />
                                                </div>
                                                <div className="step-number text-wrap visible-desktop">{resources?.TicketSelection}</div>
                                            </div>
                                            <div className="line"></div>
                                        </div>

                                        <div className="progress-step">
                                            <div>
                                                <div className="image">
                                                    <img src={imgIconProgressGrey} alt="Step 3" />
                                                </div>
                                                <div className="step-number text-wrap visible-desktop">{resources?.SummaryAndContact}</div>
                                            </div>
                                            <div className="line"></div>
                                        </div>

                                        <div className="progress-step">
                                            <div>
                                                <div className="image">
                                                    <img src={imgIconProgressGrey} alt="Step 4" />
                                                </div>
                                                <div className="step-number text-wrap visible-desktop">{resources?.PaymentAndDelivery}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mt-5 sticky-wrapper">
                                        <div className="col-md-8">
                                            <p className="text-50-55 black-color font-500">{resources?.MariaAbramovic}</p>
                                            <p className="text-30-36-bold black-color">{resources?.AbramovicEventDateRange}</p>
                                        </div>
                                    </div>
                                    <br />

                                    <div className="grey-line"></div>
                                    <div className="mt-5 mb-5 row visible-desktop">
                                        <div className="col">
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <img className="hinweis-icon" src={imgTriggerWarning} alt="Time" />
                                                </div>
                                                <div className="col-md-9">
                                                    <p className="ml-5 text-16-20 dark-grey-color font-900">{resources?.TicketWarning1Header}</p>
                                                    <p className="ml-5 text-16-18 dark-grey-color hinWeis-description"> {resources?.ticketWarning1Descr}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <img className="hinweis-icon" src={imgTimeIcon} alt="No Photography" />
                                                </div>
                                                <div className="col-md-9">
                                                    <p className="ml-5 text-16-20 dark-grey-color font-900">{resources?.TicketWarning2Header}</p>
                                                    <p className="ml-5 text-16-18 dark-grey-color hinWeis-description"> {resources?.ticketWarning2Descr}</p></div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <img className="hinweis-icon" src={imgCoatroom} alt="Time" />
                                                </div>
                                                <div className="col-md-9">
                                                    <p className="ml-5 text-16-20 dark-grey-color font-900">{resources?.TicketWarning3Header}</p>
                                                    <p className="ml-5 text-16-18 dark-grey-color hinWeis-description"> {resources?.ticketWarning3Descr}</p></div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <img className="hinweis-icon" src={imgLivePerformance} alt="Time" />
                                                </div>
                                                <div className="col-md-9">
                                                    <p className="ml-5 text-16-20 dark-grey-color font-900">{resources?.TicketWarning4Header}</p>
                                                    <p
                                                        className="ml-5 text-16-18 dark-grey-color hinWeis-description"
                                                        dangerouslySetInnerHTML={{ __html: resources?.ticketWarning4Descr }}
                                                    ></p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <img className="hinweis-icon" src={imgNoPhotography} alt="Time" />
                                                </div>
                                                <div className="col-md-9">
                                                    <p className="ml-5 text-16-20 dark-grey-color font-900">{resources?.TicketWarning5Header}</p>
                                                    <p className="ml-5 hinWeis-description text-16-18 dark-grey-color "> {resources?.ticketWarning5Descr}</p></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5 mb-5 visible-mobile">
                                        <div className="row">
                                            <div className="col-2">
                                                <img className="hinweis-icon" src={imgTriggerWarning} alt="Matthew Wong � Vincent Van Gogh" />
                                            </div>
                                            <div className="col-10">
                                                <p className="ml-5 text-16-18 dark-grey-color font-900">{resources?.TicketWarning1Header}</p>
                                                <p className="ml-5 text-16-18 dark-grey-color hinWeis-description"> {resources?.ticketWarning1Descr}</p>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-2">
                                                <img className="hinweis-icon" src={imgTimeIcon} alt="No Photography" />
                                            </div>
                                            <div className="col-10">
                                                <p className="ml-5 text-16-18 dark-grey-color font-900">{resources?.TicketWarning2Header}</p>
                                                <p className="ml-5 text-16-18 dark-grey-color hinWeis-description"> {resources?.ticketWarning2Descr}</p>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-2">
                                                <img className="hinweis-icon" src={imgCoatroom} alt="No Photography" />
                                            </div>
                                            <div className="col-10">
                                                <p className="ml-5 text-16-18 dark-grey-color font-900">{resources?.TicketWarning3Header}</p>
                                                <p className="ml-5 flex-wrap text-16-18 dark-grey-color hinWeis-description"> {resources?.ticketWarning3Descr}</p>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-2">
                                                <img className="hinweis-icon" src={imgLivePerformance} alt="No Photography" />
                                            </div>
                                            <div className="col-10">
                                                <p className="ml-5 text-16-18 dark-grey-color font-900">{resources?.TicketWarning4Header}</p>
                                                <p
                                                    className="ml-5 flex-wrap text-16-18 dark-grey-color hinWeis-description"
                                                    dangerouslySetInnerHTML={{ __html: resources?.ticketWarning4Descr }}
                                                ></p>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-2">
                                                <img className="hinweis-icon" src={imgNoPhotography} alt="No Photography" />
                                            </div>
                                            <div className="col-10">
                                                <p className="ml-5 text-16-18 dark-grey-color font-900">{resources?.TicketWarning5Header}</p>
                                                <p className="ml-5 flex-wrap text-16-18 dark-grey-color hinWeis-description"> {resources?.ticketWarning5Descr}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grey-line"></div>
                                    {/*<div className="mt-5">*/}
                                    {/*    <div className="d-flex">*/}
                                    {/*        <p className="text-20-22 dark-grey-color flex-wrap"*/}
                                    {/*            dangerouslySetInnerHTML={{ __html: resources?.PreviewText1 }}></p> &nbsp;*/}
                                    {/*        <a href={anleitungpdf} className="text-20-22 dark-grey-color anchor-no-hover" target="_blank" rel="noreferrer">*/}
                                    {/*            <span className="background-yellow">{resources?.PreviewDownloadText}</span>*/}
                                    {/*        </a>*/}
                                    {/*    </div>*/}
                                    {/*    <div>*/}
                                    {/*        <p className="text-20-22 dark-grey-color flex-wrap"*/}
                                    {/*            dangerouslySetInnerHTML={{ __html: resources?.PreviewText2 }}></p>*/}
                                    {/*    </div>*/}

                                    {/*</div>*/}
                                    <div className="mt-5">
                                        <p className="text-20-22 dark-grey-color flex-wrap">{resources?.SelectDayForExhibition}</p>
                                    </div>
                                    <div className="row mt-4">
                                        <div className="col-12 col-md-8">
                                            <div className={loading ? "isHidden" : ""}>
                                                <Calendar
                                                    view="month"
                                                    value={date}
                                                    onChange={setDate}
                                                    tileClassName={tileClassName}
                                                    onClickDay={onClickDay}
                                                    locale={language}
                                                    minDate={new Date(Date.UTC(zurichDate.getFullYear(), defaultMonth, 1))}
                                                    onActiveStartDateChange={handleActiveStartDateChange}
                                                    formatMonthYear={formatMonth}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4 visible-desktop">
                                            {!loading && selectedDate && selectedDate !== "" && (
                                                <div className="ticketing-timeslots-container">
                                                    <div className="pt-2 pb-2">
                                                        <div className="row">
                                                            <div className="col-md-12">
                                                                <p className="text-30-36-medium black-color padding-left-20 mt-2 text-center">{new DateTimeManager(selectedDateUTC).getDateStringFormat()}</p>
                                                                <p className="timeslot-div-sub-title-bold">{resources?.TicketCalendarChooseTime}</p>
                                                            </div>
                                                        </div>

                                                        {mapEventStartTimes()}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="row mt-5 mb-4">
                                        <div className="col-6">
                                            <button className="btn transperant-grey-button" onClick={() => navigate(-1)}>
                                                {resources?._Back}
                                            </button>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        )}
                    </main>
                    <Footer />
                </div>
                <div className="visible-mobile">
                    {!loading && selectedDate && (
                        <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                            <div className="modal-dialog modal-fullscreen" role="document">
                                <div className="modal-content timeslots-mobile-container">
                                    <div className="modal-header no-border">
                                        <button
                                            type="button"
                                            className="timeslots-mobile-close-btn"
                                            aria-label="Close"
                                            onClick={() => closeTimeslotPopup()} >
                                            <img src={imgClose} alt="X" />
                                        </button>
                                    </div>
                                    <div className="modal-body">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <div>
                                                    <div className="row">
                                                        <p className="text-30-36-medium black-color padding-left-20 mt-2 text-center">{new DateTimeManager(selectedDateUTC).getDateStringFormat()}</p>
                                                        <p className="timeslot-div-sub-title-bold-mobile">{resources?.TicketCalendarChooseTime}</p>
                                                    </div>
                                                    {mapEventStartTimes()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </>
        </div >
    );
};

export default Step10_Calendar_Abramovic;